import { getClerkInstance } from '../auth/clerkAuth';
async function aiAuthHeaders() {
  const token = await getClerkInstance()?.getToken();
  if (!token) throw new Error('Please sign in to use AI.');
  return { Authorization: `Bearer ${token}` };
}
/**
 * AI Chat Service - AI integration for trading analysis
 */

import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || window.location.origin;

class AIChatService {
    constructor() {
        this.apiKey = null;
        this.apiKeyFetched = false;
        this.chatHistoryKey = 'ai_chat_history';
        this.chatHistory = [];
        this.useLocalFallback = false;
        this.fullContextSent = false;
        this.lastFullContext = null;
        this.currentSymbol = null;
        this.currentExchange = null;
        this.memoryKey = 'ai_chat_memory';
        this.memory = {};
        this.messageCount = 0;
        this.lastConfidenceScore = null;
        this.aiEnabled = false;
        this.marketDataSent = false;
        this.compressedMarketData = null;
        this.MAX_CONTEXT_TOKENS = 12000; // Hard token budget
        this.MAX_HISTORY_MESSAGES = 20; // Limit history to prevent storage bloat
        this.MAX_MEMORY_ITEMS = 50; // Limit memory items
        this.sessionId = this.generateSessionId(); // Unique session ID
    }

    /**
     * Generate unique session ID
     */
    generateSessionId() {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Clean up localStorage to prevent bloat
     */
    cleanupLocalStorage() {
        try {
            // Trim chat history
            if (this.chatHistory.length > this.MAX_HISTORY_MESSAGES) {
                this.chatHistory = this.chatHistory.slice(-this.MAX_HISTORY_MESSAGES);
                this.saveChatHistory();
            }

            // Trim memory items
            const memoryKeys = Object.keys(this.memory);
            if (memoryKeys.length > this.MAX_MEMORY_ITEMS) {
                // Keep most recent items based on a simple heuristic
                const keysToKeep = memoryKeys.slice(-this.MAX_MEMORY_ITEMS);
                const newMemory = {};
                keysToKeep.forEach(key => {
                    newMemory[key] = this.memory[key];
                });
                this.memory = newMemory;
                this.saveMemory();
            }
        } catch (error) {
            console.warn('[AIChat] Error during localStorage cleanup:', error);
        }
    }

    /**
     * Fetch API key from backend
     */
    async fetchApiKeyFromBackend() {
        if (this.apiKeyFetched) return this.apiKey;
        try {
            const response = await fetch(`${API_BASE}/api/ai-config`, { headers: await aiAuthHeaders() });
            if (response.ok) {
                const data = await response.json();
                if (data.configured) {
                    this.apiKey = 'server-managed';
                    this.apiKeyFetched = true;
                    return this.apiKey;
                } else {
                    console.error('[AIChatService] No apiKey in response:', data);
                }
            } else {
                const errorText = await response.text();
                console.error('[AIChatService] Backend error response:', errorText);
            }
        } catch (error) {
            console.error('[AIChatService] Failed to fetch API key from backend:', error);
        }
        return null;
    }

    /**
     * Get API key (fetched from backend)
     */
    async getApiKey() {
        if (!this.apiKey) {
            await this.fetchApiKeyFromBackend();
        }
        return this.apiKey;
    }

    /**
     * Check if AI is enabled
     * @param {string} authToken - Clerk JWT token for authentication
     */
    async isEnabled(authToken = null) {
        if (authToken) {
            try {
                const response = await fetch(`${API_BASE}/api/user/settings`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    this.aiEnabled = data.aiEnabled !== false; // Default to true if not set
                    return this.aiEnabled;
                }
            } catch (error) {
                console.error('[AIChatService] Failed to fetch user settings:', error);
            }
        }
        // Fallback to localStorage if no token or API fails
        const enabled = localStorage.getItem('ai_chat_enabled');
        this.aiEnabled = enabled === null ? true : enabled === 'true';
        return this.aiEnabled;
    }

    /**
     * Set AI enabled/disabled
     */
    setEnabled(enabled) {
        this.aiEnabled = enabled;
        localStorage.setItem('ai_chat_enabled', enabled ? 'true' : 'false');
    }

    /**
     * Get AI credits status from backend
     */
    async getCredits(userId) {
        try {
            const response = await fetch(`${API_BASE}/api/ai-credits`, {
                headers: {
                    ...(await aiAuthHeaders()),
                },
            });
            if (response.ok) {
                return await response.json();
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch credits:', error);
            return null;
        }
    }

    /**
     * Deduct 1 credit for sending a message
     */
    async deductCredit(userId) {
        try {
            const response = await fetch(`${API_BASE}/api/ai-credits/deduct`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(await aiAuthHeaders()),
                },
            });
            if (response.ok) {
                return await response.json();
            }
            const error = await response.json();
            throw new Error(error.error || 'Failed to deduct credit');
        } catch (error) {
            console.error('Failed to deduct credit:', error);
            throw error;
        }
    }

    /**
     * Load chat history from localStorage for a specific symbol
     */
    async loadChatHistory(symbol = null, exchange = null) {
        try {
            this.currentSymbol = symbol;
            this.currentExchange = exchange;
            const key = symbol ? `${this.chatHistoryKey}_${symbol}_${exchange || 'default'}` : this.chatHistoryKey;
            const history = localStorage.getItem(key);
            if (history) {
                this.chatHistory = JSON.parse(history);
            } else {
                this.chatHistory = [];
            }

            await this.loadMessageState(symbol, exchange);
            await this.loadMemory(symbol, exchange);

            return this.chatHistory;
        } catch (error) {
            console.error('Failed to load chat history:', error);
            return [];
        }
    }

    /**
     * Get current chat history
     */
    getChatHistory() {
        return this.chatHistory;
    }

    /**
     * Save chat history to localStorage for the current symbol
     */
    async saveChatHistory(history) {
        try {
            this.chatHistory = history;
            const key = this.currentSymbol ? `${this.chatHistoryKey}_${this.currentSymbol}_${this.currentExchange || 'default'}` : this.chatHistoryKey;
            localStorage.setItem(key, JSON.stringify(history));
        } catch (error) {
            console.error('Failed to save chat history:', error);
        }
    }

    /**
     * Clear chat history for the current symbol
     */
    async clearChatHistory() {
        try {
            this.chatHistory = [];
            const key = this.currentSymbol ? `${this.chatHistoryKey}_${this.currentSymbol}_${this.currentExchange || 'default'}` : this.chatHistoryKey;
            localStorage.removeItem(key);

            await this.resetContext();

            const messageStateKey = 'ai_chat_message_state';
            const stateKey = this.currentSymbol ? `${messageStateKey}_${this.currentSymbol}_${this.currentExchange || 'default'}` : messageStateKey;
            localStorage.removeItem(stateKey);

            await this.clearMemory();
        } catch (error) {
            console.error('Failed to clear chat history:', error);
        }
    }

    /**
     * Load memory for a specific symbol
     */
    async loadMemory(symbol = null, exchange = null) {
        try {
            const key = symbol ? `${this.memoryKey}_${symbol}_${exchange || 'default'}` : this.memoryKey;
            const memoryData = localStorage.getItem(key);
            if (memoryData) {
                this.memory = JSON.parse(memoryData);
            } else {
                this.memory = {};
            }
        } catch (error) {
            console.error('Failed to load memory:', error);
            this.memory = {};
        }
    }

    /**
     * Save memory for the current symbol
     */
    async saveMemory() {
        try {
            const key = this.currentSymbol ? `${this.memoryKey}_${this.currentSymbol}_${this.currentExchange || 'default'}` : this.memoryKey;
            localStorage.setItem(key, JSON.stringify(this.memory));
        } catch (error) {
            console.error('Failed to save memory:', error);
        }
    }

    /**
     * Clear memory for the current symbol
     */
    async clearMemory() {
        try {
            this.memory = {};
            const key = this.currentSymbol ? `${this.memoryKey}_${this.currentSymbol}_${this.currentExchange || 'default'}` : this.memoryKey;
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Failed to clear memory:', error);
        }
    }

    /**
     * Load message state
     */
    async loadMessageState(symbol = null, exchange = null) {
        try {
            const messageStateKey = 'ai_chat_message_state';
            const key = symbol ? `${messageStateKey}_${symbol}_${exchange || 'default'}` : messageStateKey;
            const stateData = localStorage.getItem(key);
            if (stateData) {
                const state = JSON.parse(stateData);
                this.messageCount = state.messageCount || 0;
                this.fullContextSent = state.fullContextSent || false;
            } else {
                this.messageCount = 0;
                this.fullContextSent = false;
            }
        } catch (error) {
            console.error('Failed to load message state:', error);
            this.messageCount = 0;
            this.fullContextSent = false;
        }
    }

    /**
     * Save message state
     */
    async saveMessageState() {
        try {
            const messageStateKey = 'ai_chat_message_state';
            const key = this.currentSymbol ? `${messageStateKey}_${this.currentSymbol}` : messageStateKey;
            const state = {
                messageCount: this.messageCount,
                fullContextSent: this.fullContextSent,
                lastUpdated: Date.now(),
            };
            localStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
            console.error('Failed to save message state:', error);
        }
    }

    /**
     * Estimate token count (rough approximation: 1 token ≈ 4 characters)
     */
    estimateTokens(text) {
        return Math.ceil(text.length / 4);
    }

    /**
     * Fetch and compress market data for all timeframes
     */
    async compressMarketData(tradingContext) {
        try {
            if (!tradingContext || !tradingContext.allTimeframeData) {
                console.warn('No timeframe data available for compression');
                return null;
            }

            const compressedData = {};
            let totalTokens = 0;

            // Process each timeframe's data
            Object.entries(tradingContext.allTimeframeData).forEach(([timeframe, tfData]) => {
                if (tfData) {
                    // Compress essential timeframe data
                    const compressed = {
                        t: tfData.trend,
                        s: tfData.signal,
                        conf: tfData.confidence,
                        p: tfData.currentPrice,
                        confCount: tfData.confirmationCount,
                        confPct: tfData.confirmationPct,
                        hasVT: tfData.hasVTSignal,
                        scoreBull: tfData.scoreBreakdown?.bullish,
                        scoreBear: tfData.scoreBreakdown?.bearish,
                        vt: tfData.confirmationIndicators?.visualTrading?.latest?.call,
                        vtEntry: tfData.confirmationIndicators?.visualTrading?.latest?.entry,
                        ema50: tfData.confirmationIndicators?.trendConfirmation?.ema50?.toFixed(2),
                        rsi: tfData.confirmationIndicators?.momentumConfirmation?.rsi14?.toFixed(2),
                        volRatio: tfData.confirmationIndicators?.volumeConfirmation?.volumeRatio?.toFixed(2),
                        atr14: tfData.confirmationIndicators?.volatilityFilter?.atr14?.toFixed(4),
                        structure: tfData.confirmationIndicators?.marketStructure?.structure,
                        macd: tfData.confirmationIndicators?.macd?.latest?.trend,
                        mom: tfData.confirmationIndicators?.momentum?.latest?.signal,
                        // Add market data if available
                        h24: tfData.high24h,
                        l24: tfData.low24h,
                        v24: tfData.volume24h,
                        chg: tfData.change,
                        chgPct: tfData.changePercent
                    };
                    
                    compressedData[timeframe] = compressed;
                    const dataStr = JSON.stringify(compressed);
                    totalTokens += this.estimateTokens(dataStr);
                }
            });

            this.compressedMarketData = compressedData;
            return {
                data: compressedData,
                estimatedTokens: totalTokens,
                timeframes: Object.keys(compressedData)
            };
        } catch (error) {
            console.error('Error compressing market data:', error);
            return null;
        }
    }

    /**
     * Get market data for AI prompt (only at session start)
     */
    async getMarketDataForPrompt(tradingContext) {
        if (this.marketDataSent && this.compressedMarketData) {
            return this.compressedMarketData;
        }

        const result = await this.compressMarketData(tradingContext);
        if (result) {
            this.marketDataSent = true;
            return result.data;
        }
        return null;
    }

    /**
     * Reset market data flag (for new sessions)
     */
    resetMarketDataFlag() {
        this.marketDataSent = false;
        this.compressedMarketData = null;
    }

    /**
     * Reset context and market data
     */
    async resetContext() {
        this.fullContextSent = false;
        this.lastFullContext = null;
        this.messageCount = 0;
        this.resetMarketDataFlag();
        await this.saveMessageState();
    }

    /**
     * Create production-grade timeframe profile
     */
    createTimeframeProfile(primaryTimeframe, allTimeframeData) {
        if (!primaryTimeframe) return null;

        // Common timeframes only - remove rare ones like 12H
        const commonTimeframes = ['1m', '3m', '5m', '15m', '1H', '4H', '1D', '1W'];
        
        // Trading intent classification
        const intentMap = {
            'scalp': { timeframes: ['1m', '3m', '5m'], purpose: 'fast execution' },
            'intraday': { timeframes: ['15m', '1H'], purpose: 'day trading' },
            'swing': { timeframes: ['4H', '1D'], purpose: 'multi-day moves' },
            'position': { timeframes: ['1W', '1M'], purpose: 'investing/trend holding' }
        };

        // Determine trading intent
        let tradingIntent = 'intraday'; // default
        for (const [intent, config] of Object.entries(intentMap)) {
            if (config.timeframes.includes(primaryTimeframe)) {
                tradingIntent = intent;
                break;
            }
        }

        // Adjacency map for dynamic nearby-TF logic
        const adjacencyMap = {
            '1m': { lower: [], higher: ['3m', '5m'] },
            '3m': { lower: ['1m'], higher: ['5m', '15m'] },
            '5m': { lower: ['1m', '3m'], higher: ['15m', '1H'] },
            '15m': { lower: ['3m', '5m'], higher: ['1H', '4H'] },
            '1H': { lower: ['5m', '15m'], higher: ['4H', '1D'] },
            '4H': { lower: ['15m', '1H'], higher: ['1D', '1W'] },
            '1D': { lower: ['1H', '4H'], higher: ['1W', '1M'] },
            '1W': { lower: ['4H', '1D'], higher: ['1M'] }
        };

        const adjacency = adjacencyMap[primaryTimeframe] || { lower: [], higher: [] };

        // Determine context vs execution timeframes
        let contextTf, executionTf;
        if (tradingIntent === 'scalp') {
            contextTf = '5m';
            executionTf = '1m';
        } else if (tradingIntent === 'intraday') {
            contextTf = '1H';
            executionTf = '15m';
        } else if (tradingIntent === 'swing') {
            contextTf = '1D';
            executionTf = '4H';
        } else if (tradingIntent === 'position') {
            contextTf = '1W';
            executionTf = '1D';
        }

        // Timeframe weighting
        const weights = {
            primary: 0.50,
            higher: 0.35,
            lower: 0.15
        };

        return {
            class: tradingIntent,
            primary: primaryTimeframe,
            lower: adjacency.lower,
            higher: adjacency.higher,
            executionTf: executionTf,
            contextTf: contextTf,
            weights: weights,
            intent: intentMap[tradingIntent]
        };
    }

    /**
     * Get relevant timeframes based on adjacency and weighting
     */
    getRelevantTimeframes(primaryTimeframe, allTimeframeData) {
        const profile = this.createTimeframeProfile(primaryTimeframe, allTimeframeData);
        if (!profile) return [];

        const allTimeframes = Object.keys(allTimeframeData || {});
        
        // Get relevant timeframes using adjacency map
        const relevantTimeframes = [
            profile.primary,
            ...profile.lower,
            ...profile.higher
        ].filter(tf => allTimeframes.includes(tf));

        return relevantTimeframes;
    }

    /**
     * Analyze timeframe conflicts with improved severity classification
     */
    analyzeTimeframeConflicts(timeframeData, profile) {
        if (!timeframeData || !profile) return null;

        const primaryTf = profile.primary;
        const primaryData = timeframeData[primaryTf];
        const higherData = profile.higher.map(tf => timeframeData[tf]).filter(Boolean);
        const lowerData = profile.lower.map(tf => timeframeData[tf]).filter(Boolean);

        let conflictSeverity = 'none';
        let conflictType = '';

        if (higherData.length > 0 && primaryData) {
            const higherTrend = higherData[0].trend; // Use first higher TF
            const primaryTrend = primaryData.trend;

            if (higherTrend && primaryTrend && higherTrend !== primaryTrend) {
                // Minor: Lower TF pullback (counter-trend bounce within HTF trend)
                if (lowerData.length > 0 && this.isCounterTrendPullback(primaryTrend, lowerData)) {
                    conflictSeverity = 'minor';
                    conflictType = 'counter-trend bounce';
                }
                // Moderate: Structure disagreement between nearby TFs
                else if (this.isNearbyTfDisagreement(primaryTrend, higherData, lowerData)) {
                    conflictSeverity = 'moderate';
                    conflictType = 'structure disagreement';
                }
                // Major: All HTFs opposite to primary trend
                else if (this.isAllHtfOpposite(primaryTrend, higherData)) {
                    conflictSeverity = 'major';
                    conflictType = 'HTF opposite trend';
                }
                // Default to moderate for mixed signals
                else {
                    conflictSeverity = 'moderate';
                    conflictType = 'mixed signals';
                }
            }
        }

        return {
            severity: conflictSeverity,
            type: conflictType,
            primaryTrend: primaryData?.trend,
            higherTrend: higherData[0]?.trend,
            htfDominant: this.checkHTFDominance(timeframeData, profile)
        };
    }

    /**
     * Check if this is a counter-trend pullback (minor conflict)
     */
    isCounterTrendPullback(primaryTrend, lowerData) {
        if (!lowerData.length) return false;
        
        // Check if lower TFs are opposite to primary but weak
        const oppositeCount = lowerData.filter(d => 
            d.trend && d.trend !== primaryTrend && 
            (d.confidence || 0) < 60
        ).length;
        
        return oppositeCount >= Math.ceil(lowerData.length * 0.6);
    }

    /**
     * Check if there's disagreement between nearby timeframes (moderate conflict)
     */
    isNearbyTfDisagreement(primaryTrend, higherData, lowerData) {
        const nearbyTfs = [...higherData.slice(0, 2), ...lowerData.slice(0, 2)]; // Only nearby TFs
        if (nearbyTfs.length < 2) return false;
        
        const disagreeingTfs = nearbyTfs.filter(d => d.trend && d.trend !== primaryTrend);
        return disagreeingTfs.length >= Math.ceil(nearbyTfs.length * 0.5);
    }

    /**
     * Check if all higher timeframes oppose primary trend (major conflict)
     */
    isAllHtfOpposite(primaryTrend, higherData) {
        if (higherData.length === 0) return false;
        
        const oppositeCount = higherData.filter(d => 
            d.trend && d.trend !== primaryTrend && 
            d.trend !== 'CONSOLIDATING'
        ).length;
        
        return oppositeCount === higherData.length;
    }

    /**
     * Check HTF dominance rule
     */
    checkHTFDominance(timeframeData, profile) {
        if (!timeframeData || !profile) return false;

        const primaryData = timeframeData[profile.primary];
        const higherData = profile.higher.map(tf => timeframeData[tf]).filter(Boolean);

        if (!primaryData || higherData.length === 0) return false;

        const primaryConfidence = primaryData.confidence || 0;
        const higherConfidence = higherData[0].confidence || 0;

        // HTF dominates unless lower TF has strong breakout confirmation
        const hasStrongBreakout = primaryData.confidence >= 80 && 
                               primaryData.confirmationCount >= 5;

        return higherConfidence > primaryConfidence && !hasStrongBreakout;
    }

    /**
     * Detect market regime from timeframe data
     */
    detectMarketRegime(timeframeData, profile) {
        if (!timeframeData || !profile) return 'unknown';

        const relevantTfs = [profile.primary, ...profile.lower, ...profile.higher];
        let bullishCount = 0;
        let bearishCount = 0;
        let consolidatingCount = 0;
        let volatileCount = 0;
        let totalValidTfs = 0;

        // Analyze trend distribution across relevant timeframes
        for (const tf of relevantTfs) {
            const data = timeframeData[tf];
            if (data && data.trend) {
                totalValidTfs++;
                
                if (data.trend === 'BULLISH') bullishCount++;
                else if (data.trend === 'BEARISH') bearishCount++;
                else if (data.trend === 'CONSOLIDATING') consolidatingCount++;
                
                // Check for volatility (high ATR indicates volatility)
                if (data.confirmationIndicators?.volatilityFilter?.atr14) {
                    const atr = data.confirmationIndicators.volatilityFilter.atr14;
                    const price = data.currentPrice || 0;
                    const atrPercent = price > 0 ? (atr / price) * 100 : 0;
                    
                    if (atrPercent > 2) { // High volatility threshold
                        volatileCount++;
                    }
                }
            }
        }

        if (totalValidTfs === 0) return 'unknown';

        const bullishRatio = bullishCount / totalValidTfs;
        const bearishRatio = bearishCount / totalValidTfs;
        const consolidatingRatio = consolidatingCount / totalValidTfs;
        const volatileRatio = volatileCount / totalValidTfs;

        // Determine regime based on patterns
        if (volatileRatio > 0.6) {
            return 'volatile';
        } else if (consolidatingRatio > 0.6) {
            // Check if it's compression (pre-breakout)
            const isCompressed = this.detectCompressionState(timeframeData, profile);
            return isCompressed ? 'compressed' : 'ranging';
        } else if (bullishRatio > 0.7) {
            return 'trending_bullish';
        } else if (bearishRatio > 0.7) {
            return 'trending_bearish';
        } else if (bullishRatio > 0.4 && bearishRatio > 0.4) {
            // Mixed signals could indicate reversal zone
            return 'reversal';
        } else {
            return 'ranging';
        }
    }

    /**
     * Detect timeframe compression state with ATR filter
     */
    detectCompressionState(timeframeData, profile) {
        if (!timeframeData || !profile) return false;

        const relevantTfs = [profile.primary, ...profile.lower, ...profile.higher];
        let consolidatingCount = 0;
        let lowAtrCount = 0;
        let tightRangeCount = 0;

        for (const tf of relevantTfs) {
            const data = timeframeData[tf];
            if (data) {
                // Check for consolidation
                if (data.trend === 'CONSOLIDATING') {
                    consolidatingCount++;
                }

                // Check for low ATR (indicating compression)
                if (data.confirmationIndicators?.volatilityFilter?.atr14) {
                    const atr = data.confirmationIndicators.volatilityFilter.atr14;
                    const price = data.currentPrice || 0;
                    
                    if (price > 0) {
                        const atrPercent = (atr / price) * 100;
                        
                        // Low ATR threshold varies by timeframe
                        const atrThresholds = {
                            '1m': 0.5, '3m': 0.8, '5m': 1.0, '15m': 1.2,
                            '1H': 1.5, '4H': 2.0, '1D': 3.0, '1W': 5.0
                        };
                        
                        const threshold = atrThresholds[tf] || 1.5;
                        if (atrPercent < threshold) {
                            lowAtrCount++;
                        }
                    }
                }

                // Check for tight price range (using high/low data if available)
                if (data.confirmationIndicators?.marketStructure?.recentHigh && 
                    data.confirmationIndicators?.marketStructure?.recentLow) {
                    const high = data.confirmationIndicators.marketStructure.recentHigh;
                    const low = data.confirmationIndicators.marketStructure.recentLow;
                    const price = data.currentPrice || 0;
                    
                    if (price > 0) {
                        const rangePercent = ((high - low) / price) * 100;
                        
                        // Tight range threshold
                        if (rangePercent < 2) { // Less than 2% range is tight
                            tightRangeCount++;
                        }
                    }
                }
            }
        }

        // Enhanced compression detection:
        // 1. Multiple TFs consolidating
        // 2. Low ATR across TFs  
        // 3. Tight price ranges
        const consolidationThreshold = Math.ceil(relevantTfs.length * 0.6);
        const atrThreshold = Math.ceil(relevantTfs.length * 0.5);
        const rangeThreshold = Math.ceil(relevantTfs.length * 0.4);

        const hasConsolidation = consolidatingCount >= consolidationThreshold;
        const hasLowAtr = lowAtrCount >= atrThreshold;
        const hasTightRange = tightRangeCount >= rangeThreshold;

        // Compression requires at least 2 of 3 conditions
        const compressionScore = [hasConsolidation, hasLowAtr, hasTightRange].filter(Boolean).length;
        
        return compressionScore >= 2;
    }

    /**
     * Check for stale data and add warnings
     */
    checkDataStaleness(allTimeframeData) {
        const now = Date.now();
        const staleWarnings = [];
        const staleThresholds = {
            '1m': 2 * 60 * 1000,      // 2 minutes
            '3m': 5 * 60 * 1000,      // 5 minutes
            '5m': 10 * 60 * 1000,     // 10 minutes
            '15m': 20 * 60 * 1000,    // 20 minutes
            '1H': 45 * 60 * 1000,     // 45 minutes
            '4H': 2 * 60 * 60 * 1000, // 2 hours
            '1D': 6 * 60 * 60 * 1000, // 6 hours
            '1W': 24 * 60 * 60 * 1000 // 24 hours
        };

        if (!allTimeframeData) return staleWarnings;

        for (const [tf, data] of Object.entries(allTimeframeData)) {
            if (data && data.timestamp) {
                const dataAge = now - data.timestamp;
                const threshold = staleThresholds[tf];
                
                if (threshold && dataAge > threshold) {
                    const ageMinutes = Math.round(dataAge / (60 * 1000));
                    staleWarnings.push({
                        timeframe: tf,
                        ageMinutes: ageMinutes,
                        severity: ageMinutes > threshold * 2 ? 'critical' : 'warning'
                    });
                }
            }
        }

        return staleWarnings;
    }

    /**
     * Build context from trading data
     */
    buildTradingContext(data) {
        const {
            symbol,
            exchange,
            timeframe,
            currentPrice,
            instrumentData,
            technicalData,
            latestSignal,
            allTimeframeData,
            alignmentScore,
            chartConfig,
        } = data;

        // Check for stale data
        const staleWarnings = this.checkDataStaleness(allTimeframeData);

        return {
            symbol: symbol || 'N/A',
            exchange: exchange || 'N/A',
            timeframe: timeframe || 'N/A',
            currentPrice: currentPrice || instrumentData?.currentPrice || 'N/A',
            marketData: {
                high24h: instrumentData?.high24h || 'N/A',
                low24h: instrumentData?.low24h || 'N/A',
                volume24h: instrumentData?.volume24h || 'N/A',
                change: instrumentData?.change || 'N/A',
                changePercent: instrumentData?.changePercent || 'N/A',
            },
            technicalIndicators: technicalData || {},
            latestSignal: latestSignal || null,
            allTimeframeData: allTimeframeData || {},
            alignmentScore: alignmentScore || 0,
            chartConfig: chartConfig || null,
            dataWarnings: staleWarnings,
            timestamp: Date.now(),
        };
    }

    /**
     * Build user prompt with context
     */
    async buildUserPrompt(context, userMessage, includeMarketData = false) {
        // Create professional-grade timeframe profile
        const tfProfile = this.createTimeframeProfile(context.timeframe, context.allTimeframeData);
        
        // Get relevant timeframes using adjacency map
        const relevantTimeframes = this.getRelevantTimeframes(context.timeframe, context.allTimeframeData);
        
        // Analyze timeframe conflicts
        const conflictAnalysis = this.analyzeTimeframeConflicts(context.allTimeframeData, tfProfile);
        
        // Detect compression state and market regime
        const isCompressed = this.detectCompressionState(context.allTimeframeData, tfProfile);
        const marketRegime = this.detectMarketRegime(context.allTimeframeData, tfProfile);
        
        const compressedContext = {
            s: context.symbol,
            e: context.exchange,
            tf: context.timeframe,
            tfProfile: tfProfile,
            p: context.currentPrice,
            m: {
                h: context.marketData?.high24h,
                l: context.marketData?.low24h,
                v: context.marketData?.volume24h,
                c: context.marketData?.change,
                cp: context.marketData?.changePercent,
            },
            align: context.alignmentScore || 0,
            regime: marketRegime,
            warnings: context.dataWarnings || [],
            ts: context.timestamp || Date.now(),
            tfd: context.allTimeframeData ? relevantTimeframes.reduce((acc, tf) => {
                const tfData = context.allTimeframeData[tf];
                if (tfData) {
                    acc[tf] = {
                        t: tfData.trend,
                        s: tfData.signal,
                        conf: tfData.confidence,
                        p: tfData.currentPrice,
                        confCount: tfData.confirmationCount,
                        confPct: Math.round(tfData.confirmationPct),
                        hasVT: tfData.hasVTSignal,
                        scoreBull: tfData.scoreBreakdown?.bullish,
                        scoreBear: tfData.scoreBreakdown?.bearish,
                        vt: tfData.confirmationIndicators?.visualTrading?.latest?.call,
                        vtEntry: tfData.confirmationIndicators?.visualTrading?.latest?.entry,
                        ema50: tfData.confirmationIndicators?.trendConfirmation?.ema50?.toFixed(2),
                        rsi: tfData.confirmationIndicators?.momentumConfirmation?.rsi14?.toFixed(2),
                        volRatio: tfData.confirmationIndicators?.volumeConfirmation?.volumeRatio?.toFixed(2),
                        atr14: tfData.confirmationIndicators?.volatilityFilter?.atr14?.toFixed(4),
                        structure: tfData.confirmationIndicators?.marketStructure?.structure,
                        macd: tfData.confirmationIndicators?.macd?.latest?.trend,
                        mom: tfData.confirmationIndicators?.momentum?.latest?.signal,
                    };
                }
                return acc;
            }, {}) : {},
            conflict: conflictAnalysis,
            compression: isCompressed,
        };

        let prompt = `Context:\n${JSON.stringify(compressedContext)}\n\nQuestion:\n${userMessage}`;

        // Note: tfd in compressedContext already contains all necessary timeframe data
        // No need for duplicate MARKET DATA section

        return prompt;
    }

    /**
     * Compute calibrated confidence score from market data
     */
    computeCalibratedConfidence(context) {
        if (!context || !context.allTimeframeData) return 50; // Default confidence

        let totalConfidence = 0;
        let tfCount = 0;
        let htfAgreement = 0;
        let alignmentBonus = 0;

        const tfProfile = this.createTimeframeProfile(context.timeframe, context.allTimeframeData);
        const relevantTfs = this.getRelevantTimeframes(context.timeframe, context.allTimeframeData);

        // Calculate average confidence from relevant timeframes
        for (const tf of relevantTfs) {
            const tfData = context.allTimeframeData[tf];
            if (tfData && tfData.confidence) {
                totalConfidence += tfData.confidence;
                tfCount++;
            }
        }

        const avgConfPct = tfCount > 0 ? (totalConfidence / tfCount) * 0.5 : 0;

        // Check HTF agreement
        if (tfProfile && relevantTfs.length > 1) {
            const primaryTf = tfProfile.primary;
            const primaryData = context.allTimeframeData[primaryTf];
            const higherData = tfProfile.higher.map(tf => context.allTimeframeData[tf]).filter(Boolean);
            
            if (primaryData && higherData.length > 0) {
                const primaryTrend = primaryData.trend;
                const higherTrend = higherData[0].trend;
                
                if (primaryTrend && higherTrend && primaryTrend === higherTrend) {
                    htfAgreement = 10; // Bonus for HTF alignment
                }
            }
        }

        // Alignment score bonus
        alignmentBonus = (context.alignmentScore || 0) * 20;

        // Calculate final calibrated confidence
        const finalConfidence = Math.round(
            alignmentBonus + avgConfPct + htfAgreement
        );

        return Math.min(100, Math.max(0, finalConfidence));
    }

    /**
     * Extract confidence score from response (fallback if AI provides one)
     */
    extractConfidenceScore(response) {
        const confidenceRegex = /CONFIDENCE:\s*(\d+)/i;
        const match = response.match(confidenceRegex);

        if (match) {
            const score = parseInt(match[1], 10);
            const cleanedResponse = response.replace(confidenceRegex, '').trim();
            this.lastConfidenceScore = score;
            return {
                score: Math.min(100, Math.max(0, score)),
                cleanedResponse,
            };
        }

        return {
            score: null,
            cleanedResponse: response,
        };
    }

    /**
     * Extract memory from response with safe delimiters
     */
    extractMemoryFromResponse(response) {
        try {
            // Use safe delimiters instead of dangerous regex
            const memoryStartTag = '<MEMORY>';
            const memoryEndTag = '</MEMORY>';
            
            const startIndex = response.indexOf(memoryStartTag);
            const endIndex = response.indexOf(memoryEndTag);
            
            if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
                const memoryContent = response.substring(
                    startIndex + memoryStartTag.length, 
                    endIndex
                ).trim();
                
                // Validate that it looks like JSON before parsing
                if (memoryContent.startsWith('{') && memoryContent.endsWith('}')) {
                    try {
                        const memoryData = JSON.parse(memoryContent);
                        Object.assign(this.memory, memoryData);
                        this.saveMemory();
                        
                        // Remove the memory section from response
                        const cleanedResponse = response.substring(0, startIndex) + 
                                             response.substring(endIndex + memoryEndTag.length);
                        return cleanedResponse.trim();
                    } catch (jsonError) {
                        console.warn('[AIChat] Failed to parse memory JSON:', jsonError.message);
                        return response; // Return original if JSON is invalid
                    }
                }
            }
            return response;
        } catch (error) {
            console.warn('[AIChat] Memory extraction error:', error.message);
            return response;
        }
    }

    /**
     * Build permanent system prompt (core behavior only)
     */
    buildPermanentSystemPrompt() {
        return `You are a professional trading analyst. You decide the appropriate response length based on the user's question.

CONFIDENCE SCORE REQUIREMENT:
- ALWAYS start your response with a confidence score in this exact format: "CONFIDENCE: XX" where XX is a number from 0-100
- The confidence score reflects how confident you are in your analysis based on the data quality and alignment

HOW TO DECIDE RESPONSE LENGTH:

Use **SHORT RESPONSE** (2-4 lines) when user asks:
- Simple follow-up questions: "What do you mean?", "Why?", "How?"
- Brief confirmations: "Is it bullish?", "Good setup?", "What's the trend?"
- Clarifications: "Explain that", "What is X?"
- Quick status checks: "Current trend?", "RSI value?"

Use **FULL REPORT** (6 structured sections) when user asks:
- Analysis requests: "Analyze", "Full analysis", "Detailed report"
- Market assessment: "Outlook", "Market structure", "Multi timeframe"
- Comprehensive evaluation: "Evaluate setup", "Should I trade this?", "Full breakdown"
- First-time questions about the market

SHORT RESPONSE FORMAT:
- Direct answer in 2-4 lines max
- No numbered sections
- Use **bold** for key values
- Can include confidence score if relevant

Rules:
- Be precise and data-driven
- Use confidence scores appropriately
- Focus on actionable insights
- Always prioritize the primary timeframe while respecting higher timeframe directional bias
- Provide comprehensive analysis in the 'response' field - DO NOT give short responses
- Include detailed insights, risks, and actionable trading information
- Use all available market data to provide thorough analysis

IMPORTANT - REGULATORY COMPLIANCE:
- NEVER provide direct buy/sell recommendations or tell users what to do
- NEVER use phrases like "buy here", "sell here", "enter now", "exit now"
- ALWAYS frame responses as analysis and observations, not recommendations
- Use phrases like "analysis suggests", "indicators show", "market structure indicates"
- Focus on describing market conditions, signals, and levels without directing action
- Let users make their own trading decisions based on the analysis provided

STRUCTURED RESPONSE REQUIREMENTS:
- Provide detailed trading analysis in the 'response' field
- Include comprehensive timeframe analysis with signals and levels
- Add specific risks, insights, and actionable analysis (not recommendations)
- Ensure 'response' field contains full analysis text, not just summary
- Use 'summary' field for brief overview, 'response' field for detailed analysis

MEMORY UPDATE (IMPORTANT):
- At the end of your response, include a MEMORY section with important information
- Format: <MEMORY>{"key": "value", "key2": "value2"}</MEMORY>
- Include things like: user preferences, important patterns, recurring questions
- Use safe delimiters to prevent parsing errors`;
    }

    /**
     * Build dynamic analysis rules (injected only when needed)
     */
    buildDynamicAnalysisRules() {
        return `PROFESSIONAL TIMEFRAME ARCHITECTURE:

TRADING INTENT CLASSIFICATION:
- tfProfile.class: Trading intent (scalp/intraday/swing/position)
- scalp: 1m-5m for fast execution
- intraday: 15m-1H for day trading  
- swing: 4H-1D for multi-day moves
- position: 1W-1M for investing/trend holding

TIMEFRAME RELATIONSHIP MODEL:
- primaryTf: Main timeframe being analyzed (50% weight)
- higherTf: Higher timeframes for directional bias (35% weight)
- lowerTf: Lower timeframes for execution refinement (15% weight)
- executionTf: Entry/trigger timeframe
- contextTf: Macro trend timeframe

ADJACENCY MAP (dynamic nearby-TF logic):
- 1m → analyze 3m, 5m (higher)
- 3m → analyze 1m (lower), 5m, 15m (higher)
- 5m → analyze 1m, 3m (lower), 15m, 1H (higher)
- 15m → analyze 3m, 5m (lower), 1H, 4H (higher)
- 1H → analyze 5m, 15m (lower), 4H, 1D (higher)
- 4H → analyze 15m, 1H (lower), 1D, 1W (higher)
- 1D → analyze 1H, 4H (lower), 1W, 1M (higher)
- 1W → analyze 4H, 1D (lower), 1M (higher)

CONFLICT SEVERITY CLASSIFICATION:
- minor: Lower TF pullback (counter-trend bounce)
- moderate: Structure disagreement between nearby TFs
- major: HTF opposite trend (higher timeframe dominates)

HTF DOMINANCE RULE:
Higher timeframe structure overrides lower timeframe momentum
unless lower timeframe has strong breakout confirmation (conf >= 80%, 5+ confirmations)

EXECUTION vs CONTEXT SEPARATION:
- Context TF: Defines macro trend and directional bias
- Execution TF: Defines precise activation levels and triggers
- Example: 4H bullish trend (context) + 15m pullback activation (execution)

COMPRESSION DETECTION:
When multiple nearby TFs consolidate simultaneously → compression state
Increases breakout probability for strategic activation levels

WEIGHTING SYSTEM:
- Primary TF: 50% (defines setup)
- Higher TFs: 35% (directional bias)
- Lower TFs: 15% (execution refinement only)

CONFIRMATION INDICATOR SYSTEM (tfd = timeframe data):
For each relevant timeframe based on adjacency map, you receive:

PRIMARY SIGNAL (determines direction):
- vt: Visual Trading signal (BUY/SELL) - THE PRIMARY DIRECTION
- hasVT: true if VT signal exists, false if using EMA50 fallback
- vtActivationLevel: Activation level price

CONFIRMATION LAYERS (6 layers that confirm VT signal strength):
- validTrend: EMA50 aligned (price > EMA50 for BUY, < for SELL)
- validMomentum: RSI aligned (RSI >55 for BUY, <45 for SELL)
- validVolume: volumeRatio >= 1.0 (adequate participation)
- validStructure: Market structure aligned (HH/HL for BUY, LH/LL for SELL)
- MACD: MACD trend aligned with VT direction
- Momentum: Momentum indicator aligned with VT direction

Key fields per timeframe:
- confCount: Number of confirmations passed (0-6)
- confPct: Confirmation percentage (0-100%)
- conf: Overall confidence score (0-100%)

ALIGNMENT SCORE (align: 0-5):
- 5 = All relevant timeframes aligned (highest confidence)
- 4-3 = Most relevant timeframes aligned (good confidence)
- 2 = Mixed signals in relevant timeframes (low confidence)
- 0 = No relevant timeframe data available

SIGNAL CLASSIFICATION (based on VT + confirmations):
When VT signal exists (hasVT=true):
- strong_buy/sell: 5-6 confirmations passed (high confidence)
- buy/sell: 3-4 confirmations passed (moderate confidence)
- weak_buy/sell: 1-2 confirmations passed (low confidence)
- bullish/bearish_bias: 0 confirmations (VT says direction but no support)

SIGNAL VALIDITY RULES:
- STRONG: VT signal + 5-6 confirmations (confPct >= 80%)
- VALID: VT signal + 3-4 confirmations (confPct 50-80%)
- WEAK: VT signal + 1-2 confirmations (confPct 20-50%)
- BIAS ONLY: VT signal but 0 confirmations (confPct <20%)
- NO VT: Using EMA50 fallback with confirmations

FULL REPORT STRUCTURE (use when appropriate):
## 1. Market Summary
## 2. Multi-Timeframe Outlook (use tfProfile.adjacency for relevant TFs)
## 3. Signal Validity Assessment
## 4. Trade Insight
## 5. Risks
## 6. Key Levels

PROFESSIONAL ANALYSIS GUIDELINES:
- Use tfProfile.weights for balanced analysis (50% primary, 35% higher, 15% lower)
- Apply HTF dominance rule: higher TF structure overrides lower TF momentum
- Separate execution vs context timeframes in your analysis
- Check compression state for breakout opportunities
- Classify conflicts as minor/moderate/major with specific meanings
- Analyze only relevant timeframes from adjacency map, not all available TFs`;
    }

    /**
     * Estimate token count for text
     */
    estimateTokens(text) {
        // Rough estimation: ~4 characters per token
        return Math.ceil(text.length / 4);
    }

    /**
     * Dynamic trimming based on token budget
     */
    trimForTokenBudget(context, userMessage, attachments) {
        let totalTokens = 0;
        const components = [];

        // Estimate system prompt tokens
        const systemPrompt = this.buildSystemPrompt();
        totalTokens += this.estimateTokens(systemPrompt);
        components.push({ type: 'system', tokens: this.estimateTokens(systemPrompt), content: systemPrompt });

        // Estimate user prompt tokens
        let userPrompt = `Context:\n${JSON.stringify(context)}\n\nQuestion:\n${userMessage}`;
        totalTokens += this.estimateTokens(userPrompt);
        components.push({ type: 'user', tokens: this.estimateTokens(userPrompt), content: userPrompt });

        // Estimate attachment tokens
        if (attachments && attachments.length > 0) {
            const attachmentTokens = attachments.reduce((sum, att) => sum + this.estimateTokens(att.content), 0);
            totalTokens += attachmentTokens;
            components.push({ type: 'attachments', tokens: attachmentTokens, content: attachments });
        }

        // Estimate history tokens
        const recentHistory = this.chatHistory.slice(-4);
        let historyTokens = 0;
        const trimmedHistory = [];
        
        for (const msg of recentHistory) {
            const msgTokens = this.estimateTokens(msg.content);
            if (totalTokens + msgTokens <= this.MAX_CONTEXT_TOKENS) {
                historyTokens += msgTokens;
                trimmedHistory.push(msg);
                totalTokens += msgTokens;
                components.push({ type: 'history', tokens: msgTokens, content: msg });
            } else {
                // Trim history first to stay within budget
                break;
            }
        }

        // If still over budget, trim attachments
        if (totalTokens > this.MAX_CONTEXT_TOKENS && attachments) {
            const attachmentComponent = components.find(c => c.type === 'attachments');
            if (attachmentComponent) {
                // Reduce attachment verbosity
                const reducedAttachments = attachments.map(att => ({
                    ...att,
                    content: att.content.length > 1000 ? att.content.substring(0, 1000) + '...' : att.content
                }));
                totalTokens -= attachmentComponent.tokens;
                totalTokens += this.estimateTokens(JSON.stringify(reducedAttachments));
                attachmentComponent.content = reducedAttachments;
            }
        }

        return {
            trimmedHistory,
            trimmedAttachments: components.find(c => c.type === 'attachments')?.content || attachments,
            totalTokens,
            withinBudget: totalTokens <= this.MAX_CONTEXT_TOKENS
        };
    }

    /**
     * Build system prompt (permanent + dynamic parts)
     */
    buildSystemPrompt() {
        const memoryStr = Object.keys(this.memory).length > 0
            ? `\n\nMEMORY FROM PAST CONVERSATIONS:\n${JSON.stringify(this.memory, null, 2)}`
            : '';

        const permanentPrompt = this.buildPermanentSystemPrompt();
        const dynamicRules = this.buildDynamicAnalysisRules();

        return `${permanentPrompt}\n\n${dynamicRules}${memoryStr}`;
    }

    /**
     * Send message to AI API with attachments
     */
    async sendMessageWithAttachments(context, userMessage, attachments = [], userId) {
        // Check if AI is enabled
        if (!this.isEnabled()) {
            throw new Error('AI Chat is not enabled. Please enable it in settings.');
        }

        // Check and deduct credits if userId provided
        if (userId) {
            try {
                await this.deductCredit(userId);
            } catch (error) {
                if (error.message.includes('No credits remaining')) {
                    throw new Error('No AI credits remaining. Your monthly allocation resets next month.');
                }
                throw error;
            }
        }

        try {
            this.messageCount++;
            this.fullContextSent = true;
            this.lastFullContext = context;

            // Apply token budgeting and dynamic trimming
            const trimmed = this.trimForTokenBudget(context, userMessage, attachments);
            
            if (!trimmed.withinBudget) {
                console.warn('[AIChat] Token budget exceeded, applied dynamic trimming');
            }

            const systemPrompt = this.buildSystemPrompt();
            
            // Build user prompt with trimmed attachments content included
            let userPrompt = await this.buildUserPrompt(context, userMessage, false);
            
            // Add trimmed attachment content to user prompt if attachments exist
            if (trimmed.trimmedAttachments && trimmed.trimmedAttachments.length > 0) {
                const attachmentContent = trimmed.trimmedAttachments.map(att => {
                    try {
                        const parsedContent = JSON.parse(att.content);
                        return `\n\n--- ATTACHED FILE: ${att.name} ---\n${JSON.stringify(parsedContent, null, 2)}`;
                    } catch (e) {
                        return `\n\n--- ATTACHED FILE: ${att.name} ---\n${att.content}`;
                    }
                }).join('\n\n');
                
                userPrompt += attachmentContent;
            }

            // Build messages in correct order: system -> trimmed history -> latest user
            const messages = [
                { role: 'system', content: systemPrompt },
            ];

            // Add trimmed chat history for context BEFORE latest user message
            for (const msg of trimmed.trimmedHistory) {
                if (msg.role === 'user' || msg.role === 'assistant') {
                    messages.push({
                        role: msg.role,
                        content: msg.content,
                    });
                }
            }

            // Add latest user message AFTER history for proper continuity
            messages.push({ role: 'user', content: userPrompt });

            // Prepare request payload (temporarily disabled JSON response mode for full analysis)
            const payload = {
                model: 'syfrus-ai-server',
                messages: messages,
                temperature: 0.7,
                max_tokens: 1500,
            };

            const response = await axios.post(
                `${API_BASE}/api/ai-chat`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(await aiAuthHeaders()),
                    },
                }
            );

            if (response.data && response.data.choices && response.data.choices[0]) {
                let content = response.data.choices[0].message.content;

                // Extract AI-provided confidence score (fallback)
                const { score: aiScore, cleanedResponse } = this.extractConfidenceScore(content);

                // Compute calibrated confidence from market data
                const calibratedConfidence = this.computeCalibratedConfidence(context);

                // Use calibrated confidence as primary, AI score as fallback
                const finalConfidenceScore = calibratedConfidence || aiScore;

                // Extract and save memory
                const finalResponse = this.extractMemoryFromResponse(cleanedResponse);

                // Add system computed confidence to context for future reference
                if (calibratedConfidence) {
                }

                // Return message IDs for component to manage - component handles saving
                return {
                    content: finalResponse,
                    messageId: Date.now(),
                    confidenceScore: finalConfidenceScore,
                    systemComputedConfidence: calibratedConfidence,
                    aiProvidedConfidence: aiScore,
                };
            }

            throw new Error('Invalid response from AI API');
        } catch (error) {
            console.error('Error sending message to AI:', error);
            if (error.response) {
                throw new Error(`AI API Error: ${error.response.data?.error?.message || error.response.statusText}`);
            }
            throw error;
        }
    }

    /**
     * Send message to AI API (legacy method for backward compatibility)
     */
    async sendMessage(context, userMessage, userId) {
        // Check if AI is enabled
        if (!this.isEnabled()) {
            throw new Error('AI Chat is not enabled. Please enable it in settings.');
        }

        // Check and deduct credits if userId provided
        if (userId) {
            try {
                await this.deductCredit(userId);
            } catch (error) {
                if (error.message.includes('No credits remaining')) {
                    throw new Error('No AI credits remaining. Your monthly allocation resets next month.');
                }
                throw error;
            }
        }

        try {
            this.messageCount++;
            this.fullContextSent = true;
            this.lastFullContext = context;

            const systemPrompt = this.buildSystemPrompt();
            
            // Include market data only for first message of session
            const includeMarketData = this.messageCount === 1;
            const userPrompt = await this.buildUserPrompt(context, userMessage, includeMarketData);

            const messages = [
                { role: 'system', content: systemPrompt },
                { role: 'user', content: userPrompt },
            ];

            // Add recent chat history for context
            const recentHistory = this.chatHistory.slice(-4);
            for (const msg of recentHistory) {
                if (msg.role === 'user' || msg.role === 'assistant') {
                    messages.push({
                        role: msg.role,
                        content: msg.content,
                    });
                }
            }

            const response = await axios.post(
                `${API_BASE}/api/ai-chat`,
                {
                    model: 'syfrus-ai-server',
                    messages: messages,
                    temperature: 0.7,
                    max_tokens: 1500,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(await aiAuthHeaders()),
                    },
                }
            );

            if (response.data && response.data.choices && response.data.choices[0]) {
                let content = response.data.choices[0].message.content;

                // Extract confidence score
                const { score, cleanedResponse } = this.extractConfidenceScore(content);

                // Extract and save memory
                const finalResponse = this.extractMemoryFromResponse(cleanedResponse);

                // Return message IDs for component to manage - component handles saving
                return {
                    content: finalResponse,
                    messageId: Date.now(),
                    confidenceScore: score,
                };
            }

            throw new Error('Invalid response from AI API');
        } catch (error) {
            console.error('Error sending message to AI:', error);
            if (error.response) {
                throw new Error(`AI API Error: ${error.response.data?.error?.message || error.response.statusText}`);
            }
            throw error;
        }
    }

    /**
     * Summarize chat history
     */
    async summarizeChat() {
        if (this.chatHistory.length === 0) {
            return 'No chat history to summarize.';
        }

        try {
            const chatContent = this.chatHistory
                .filter(msg => msg.role !== 'system')
                .map(msg => `${msg.role}: ${msg.content}`)
                .join('\n\n');

            const response = await axios.post(
                `${API_BASE}/api/ai-chat`,
                {
                    model: 'syfrus-ai-server',
                    messages: [
                        {
                            role: 'system',
                            content: 'Summarize this trading analysis chat in 3-5 bullet points covering: key insights, signals discussed, and recommendations. Be concise.'
                        },
                        { role: 'user', content: chatContent },
                    ],
                    temperature: 0.5,
                    max_tokens: 500,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(await aiAuthHeaders()),
                    },
                }
            );

            if (response.data && response.data.choices && response.data.choices[0]) {
                return response.data.choices[0].message.content;
            }

            throw new Error('Failed to generate summary');
        } catch (error) {
            console.error('Error summarizing chat:', error);
            throw error;
        }
    }

    /**
     * Get last confidence score
     */
    getLastConfidenceScore() {
        return this.lastConfidenceScore;
    }

    /**
     * Check if using local fallback
     */
    isUsingLocalFallback() {
        return this.useLocalFallback;
    }

    /**
     * Get API usage stats (mock for now)
     */
    async getAPIUsageStats() {
        const callsMade = Math.floor(this.chatHistory.length / 2);
        const limit = 100;
        return {
            callsMade,
            limit,
            percentageUsed: ((callsMade / limit) * 100).toFixed(1),
        };
    }
}

// Export singleton instance
const aiChatService = new AIChatService();
export default aiChatService;
